import { useTranslation } from "react-i18next";
import Form from "@imminently/decisively-ui/Form";
import Dialog, { useDialogContext } from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import { useNotify } from "@/common/notifications";
import TextField from "@imminently/decisively-ui/TextField";
import SelectField from "@imminently/decisively-ui/SelectField";
import SwitchField from "@imminently/decisively-ui/SwitchField";
import { LOCALE_OPTIONS } from "@/modals/ProjectModal_localeOptions";
import type { Release } from "@packages/commons";
import { getReleaseUiName } from "../utils";
import { useCloneRelease, useEditRelease, usePromoteRelease } from "../hooks/useRelease";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { newReleaseSchema, type NewReleaseFormValues } from "../types";
import FormPanel from "@imminently/decisively-ui/FormPanel";

interface ReleaseModalProps {
  /** When provided, the modal will operate against this release (edit or clone). */
  baseRelease?: Release;
  /** 'edit' will call the edit endpoint, 'promote' deploys to prod, otherwise performs a clone by default */
  mode?: "edit" | "clone" | "promote";
  close: () => void;
}

export const ReleaseModal = ({ baseRelease, mode = "clone", close }: ReleaseModalProps) => {
  const { t } = useTranslation();
  const dialogContext = useDialogContext();
  const notify = useNotify();
  const fullRelease = useFullRelease();
  const effectiveBaseRelease = baseRelease ?? fullRelease ?? undefined;

  const { cloneRelease, isLoading: cloneLoading } = useCloneRelease();
  const { editRelease, isLoading: editLoading } = useEditRelease();
  const { promoteRelease, isLoading: promoteLoading } = usePromoteRelease();
  const isLoading = cloneLoading || editLoading || promoteLoading;

  const releaseName = getReleaseUiName(effectiveBaseRelease!);
  const title =
    mode === "edit"
      ? t("release_centre.edit_release")
      : mode === "promote"
        ? t("release_centre.promote_release")
        : t("release_centre.duplicate_release");
  const description =
    mode === "edit"
      ? t("release_centre.edit_description", { release: releaseName })
      : mode === "promote"
        ? t("release_centre.promote_description", { release: releaseName })
        : t("release_centre.duplicate_description", { release: releaseName });

  const defaultValues: NewReleaseFormValues = {
    description: effectiveBaseRelease?.description ?? "",
    locale: effectiveBaseRelease?.locale ?? "",
    canVector: effectiveBaseRelease?.canVector ?? false,
  };

  const onSubmit = async (values: NewReleaseFormValues) => {
    try {
      if (mode === "edit") {
        if (!effectiveBaseRelease?.id) throw new Error("No release to edit");
        await editRelease(effectiveBaseRelease.id, {
          description: values.description,
          locale: values.locale,
        });
      } else if (effectiveBaseRelease?.id) {
        const payload = {
          description: values.description,
          locale: values.locale,
          canVector: values.canVector,
        };
        if (mode === "promote") {
          await promoteRelease(effectiveBaseRelease.id, payload);
        } else {
          await cloneRelease(effectiveBaseRelease.id, payload);
        }
      }
      dialogContext?.close();
    } catch (e) {
      // if (mode === "edit") notify.error(t("errors.failed_to_update") as string);
      // else notify.error(t("errors.failed_to_duplicate") as string);
    }
  };

  return (
    <Dialog.Content className="flex flex-col">
      <DialogHeader title={title} description={description} />
      <Form
        schema={newReleaseSchema}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        <Dialog.Body asChild>
          <FormPanel className="scrollable">
            <TextField
              name="description"
              label={t("description")}
              placeholder="Optional description"
              fullWidth
            />
            <SelectField
              name="locale"
              label={t("projects.locale_label")}
              options={LOCALE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.key,
              }))}
              placeholder="Select a locale"
              fullWidth
            />
            <SwitchField
              name="canVector"
              label={t("release_centre.can_vector_label")}
            />
          </FormPanel>
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.Actions.PrimaryButton
            label={mode === "edit" ? t("save") : mode === "promote" ? t("promote_label") : t("create")}
            type="submit"
            loading={isLoading}
            disabled={isLoading}
          />
        </Dialog.Actions>
      </Form>
    </Dialog.Content>
  );
};

export default ReleaseModal;
